/**
 * EmailSettings — per-organisation sender identity & connected email account.
 * Sender name/email + 6-digit verification, and optional own-SMTP (Gmail App
 * Password or any provider) so notices genuinely send from the org's address.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, Loader2, ShieldCheck, ShieldAlert, Plug, Unplug, Send } from "lucide-react";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const inputCls = "w-full h-10 px-3 border-2 border-slate-200 bg-white text-sm text-slate-900 focus:outline-none focus:border-teal-600";

export const EmailSettings = () => {
  const [s, setS] = useState(null);
  const [senderName, setSenderName] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [savingSender, setSavingSender] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [provider, setProvider] = useState("gmail");
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("465");
  const [connecting, setConnecting] = useState(false);

  const load = async () => {
    const r = await api.get("/email-settings");
    setS(r.data);
    setSenderName(r.data.sender_name || "");
    setSenderEmail(r.data.sender_email || "");
    if (r.data.smtp_user) setSmtpUser(r.data.smtp_user);
    if (r.data.smtp_provider) setProvider(r.data.smtp_provider);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  if (!s) return null;
  const readOnly = !s.can_manage;

  const saveSender = async () => {
    setSavingSender(true);
    try {
      const r = await api.put("/email-settings", { sender_name: senderName, sender_email: senderEmail });
      setS((p) => ({ ...p, ...r.data }));
      setCodeSent(false);
      toast.success("Sender details saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingSender(false); }
  };

  const sendCode = async () => {
    try {
      const r = await api.post("/email-settings/verify");
      setCodeSent(true);
      toast.success(`Verification code sent to ${r.data.sent_to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send code"); }
  };

  const confirmCode = async () => {
    setVerifying(true);
    try {
      await api.post("/email-settings/confirm", { code });
      toast.success("Sending email verified");
      setCodeSent(false); setCode("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); }
    finally { setVerifying(false); }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      await api.put("/email-settings/smtp", {
        provider, user: smtpUser, password: smtpPassword,
        host: smtpHost, port: parseInt(smtpPort, 10) || 465,
      });
      toast.success("Connected — a confirmation email was sent to your inbox");
      setSmtpPassword("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Connection failed"); }
    finally { setConnecting(false); }
  };

  const disconnect = async () => {
    try {
      await api.delete("/email-settings/smtp");
      toast.success("Disconnected — sending falls back to the platform with your name and reply-to");
      await load();
    } catch { toast.error("Could not disconnect"); }
  };

  return (
    <div className="border-2 border-slate-200 bg-white" data-testid="email-settings-card">
      <div className="px-5 py-4 border-b-2 border-slate-200 bg-slate-50 flex items-center gap-2">
        <Mail size={16} className="text-teal-700" />
        <h2 className="font-black text-slate-900 text-sm">Email sending</h2>
        {s.smtp_connected ? (
          <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-300" data-testid="email-mode-badge">
            <Plug size={11} /> Sending from your account
          </span>
        ) : (
          <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-300" data-testid="email-mode-badge">
            Via platform — replies to you
          </span>
        )}
      </div>
      <div className="p-5 space-y-5">
        <p className="text-xs text-slate-500 leading-relaxed">
          All notices, reports and council correspondence from your organisation are sent with
          the identity below — never from the platform owner. Connect your own email account so
          messages genuinely send from your address; until then they are delivered via the
          platform with your name showing and replies directed to you.
        </p>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Sender name (person or organisation)">
            <input data-testid="sender-name-input" className={inputCls} value={senderName}
              disabled={readOnly} onChange={(e) => setSenderName(e.target.value)}
              placeholder="e.g. Fairfield City Council" />
          </Field>
          <Field label="Sending email address">
            <input data-testid="sender-email-input" className={inputCls} value={senderEmail}
              disabled={readOnly} onChange={(e) => setSenderEmail(e.target.value)}
              placeholder="notices@yourbusiness.com.au" />
          </Field>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {!readOnly && (
            <button data-testid="save-sender-btn" onClick={saveSender} disabled={savingSender}
              className="inline-flex items-center gap-2 px-4 h-9 bg-[#1E1B4B] text-white text-xs font-bold hover:bg-[#312E81] disabled:opacity-50">
              {savingSender ? <Loader2 size={13} className="animate-spin" /> : null} Save sender
            </button>
          )}
          {s.verified ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700" data-testid="sender-verified-badge">
              <ShieldCheck size={14} /> Verified
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-700" data-testid="sender-unverified-badge">
              <ShieldAlert size={14} /> Not verified
            </span>
          )}
          {!s.verified && !readOnly && !codeSent && (
            <button data-testid="send-code-btn" onClick={sendCode}
              className="inline-flex items-center gap-1.5 px-3 h-9 border-2 border-teal-600 text-teal-800 text-xs font-bold hover:bg-teal-50">
              <Send size={12} /> Send verification code
            </button>
          )}
          {codeSent && (
            <span className="inline-flex items-center gap-2">
              <input data-testid="verify-code-input" className="h-9 w-28 px-3 border-2 border-slate-200 text-sm tracking-widest font-mono"
                maxLength={6} placeholder="123456" value={code} onChange={(e) => setCode(e.target.value)} />
              <button data-testid="confirm-code-btn" onClick={confirmCode} disabled={verifying || code.length !== 6}
                className="px-3 h-9 bg-teal-700 text-white text-xs font-bold hover:bg-teal-800 disabled:opacity-50">
                {verifying ? <Loader2 size={13} className="animate-spin" /> : "Confirm"}
              </button>
            </span>
          )}
        </div>

        <div className="border-t-2 border-slate-100 pt-5">
          <h3 className="text-xs font-black uppercase tracking-wider text-slate-700 mb-1">Connect your email account</h3>
          {s.smtp_connected ? (
            <div className="flex flex-wrap items-center gap-3 mt-2">
              <span className="text-sm text-slate-700" data-testid="smtp-connected-info">
                Connected: <b>{s.smtp_user}</b> — every email sends directly from this account.
              </span>
              {!readOnly && (
                <button data-testid="disconnect-smtp-btn" onClick={disconnect}
                  className="inline-flex items-center gap-1.5 px-3 h-9 border-2 border-red-200 text-red-700 text-xs font-bold hover:bg-red-50">
                  <Unplug size={12} /> Disconnect
                </button>
              )}
            </div>
          ) : readOnly ? (
            <p className="text-xs text-slate-500">Ask your organisation owner to connect an email account.</p>
          ) : (
            <div className="space-y-3 mt-2">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Provider">
                  <select data-testid="smtp-provider-select" className={inputCls} value={provider}
                    onChange={(e) => { setProvider(e.target.value); if (e.target.value === "smtp") setSmtpPort("587"); else setSmtpPort("465"); }}>
                    <option value="gmail">Gmail / Google Workspace</option>
                    <option value="smtp">Other (Outlook / Microsoft 365 / any SMTP)</option>
                  </select>
                </Field>
                <Field label="Email address (username)">
                  <input data-testid="smtp-user-input" className={inputCls} value={smtpUser}
                    onChange={(e) => setSmtpUser(e.target.value)} placeholder="you@yourbusiness.com.au" />
                </Field>
                <Field label={provider === "gmail" ? "Google App Password (16 characters)" : "Password / app password"}>
                  <input data-testid="smtp-password-input" type="password" className={inputCls} value={smtpPassword}
                    onChange={(e) => setSmtpPassword(e.target.value)} placeholder="••••••••••••••••" />
                </Field>
                {provider === "smtp" && (
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2">
                      <Field label="SMTP host">
                        <input data-testid="smtp-host-input" className={inputCls} value={smtpHost}
                          onChange={(e) => setSmtpHost(e.target.value)} placeholder="smtp.office365.com" />
                      </Field>
                    </div>
                    <Field label="Port">
                      <input data-testid="smtp-port-input" className={inputCls} value={smtpPort}
                        onChange={(e) => setSmtpPort(e.target.value)} />
                    </Field>
                  </div>
                )}
              </div>
              {provider === "gmail" && (
                <p className="text-[11px] text-slate-500">
                  Gmail requires an <b>App Password</b> (not your normal password): Google Account →
                  Security → 2-Step Verification → App passwords. For Outlook/Microsoft 365 choose
                  &ldquo;Other&rdquo; and use host <code className="bg-slate-100 px-1">smtp.office365.com</code>, port 587.
                </p>
              )}
              <button data-testid="connect-smtp-btn" onClick={connect}
                disabled={connecting || !smtpUser || !smtpPassword || (provider === "smtp" && !smtpHost)}
                className="inline-flex items-center gap-2 px-4 h-9 bg-teal-700 text-white text-xs font-bold hover:bg-teal-800 disabled:opacity-50">
                {connecting ? <Loader2 size={13} className="animate-spin" /> : <Plug size={13} />}
                Connect &amp; send test email
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;
